import { BASE_WS_URL } from './config';

/**
 * 语音识别 WebSocket 服务 (使用 AudioContext 获取 PCM 音频数据)
 */
export class ASRService {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private audioStream: MediaStream | null = null;
  private audioInput: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private requestId: string;
  private isRecording: boolean = false;

  // 音频参数
  private readonly SAMPLE_RATE = 16000; // 16kHz 采样率
  private readonly BUFFER_SIZE = 4096; // 缓冲区大小
  private readonly CHANNELS = 1; // 声道
  private readonly CHUNK_SIZE = 1600; // 每次发送 1600 个样本（3200 字节）

  // 音频缓冲区（用于累积数据并分块发送）
  private audioBuffer: Int16Array = new Int16Array(0);

  // 心跳机制
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly HEARTBEAT_INTERVAL = 10000; // 心跳间隔 10 秒

  // 回调函数
  private onConnectedCallback?: () => void;
  private onStartCallback?: () => void;
  private onTranscriptCallback?: (text: string, isFinal: boolean) => void;
  private onErrorCallback?: (error: Error) => void;
  private onEndCallback?: () => void;
  private onDisconnectCallback?: () => void;

  constructor(requestId?: string) {
    this.requestId = requestId || this.generateRequestId();
  }

  /**
   * 生成请求 ID
   */
  private generateRequestId(): string {
    return `asr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 启动心跳
   */
  private startHeartbeat(): void {
    // 清除已有的心跳
    this.stopHeartbeat();

    console.log('[ASR] 启动心跳，间隔:', this.HEARTBEAT_INTERVAL, 'ms');

    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const heartbeat = JSON.stringify({
          type: 'ping',
          timestamp: Date.now(),
        });
        this.ws.send(heartbeat);
        console.log('[ASR] 发送心跳');
      } else {
        console.warn('[ASR] WebSocket 未连接，停止心跳');
        this.stopHeartbeat();
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * 停止心跳
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('[ASR] 心跳已停止');
    }
  }

  /**
   * 连接 WebSocket
   */
  private connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // 构建 WebSocket URL
        const token = localStorage.getItem('token');
        const wsUrl = `${BASE_WS_URL}/asr/${this.requestId}?token=Bearer ${token}`;
        console.log('[ASR] Connecting to:', wsUrl);

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('[ASR] WebSocket connected');
          this.startHeartbeat(); // 启动心跳
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('[ASR] Received:', data);

            if (data.type === 'intermediate') {
              // 中间识别结果
              this.onTranscriptCallback?.(data.text, false);
            } else if (data.type === 'complete') {
              // 识别结束
              this.onTranscriptCallback?.(data.text, true);
              console.log('[ASR] Recognition ended');
              // this.stop();
            }
          } catch (error) {
            console.error('[ASR] Failed to parse message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('[ASR] WebSocket error:', error);
          this.stopHeartbeat(); // 停止心跳
          this.onErrorCallback?.(new Error('WebSocket connection error'));
          reject(error);
        };

        this.ws.onclose = () => {
          console.log('[ASR] WebSocket closed');
          this.cleanup();
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 连接 WebSocket（分离连接和录音）
   */
  async connect(callbacks: {
    onConnected?: () => void;
    onStart?: () => void;
    onTranscript?: (text: string, isFinal: boolean) => void;
    onError?: (error: Error) => void;
    onEnd?: () => void;
    onDisconnect?: () => void;
  }): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[ASR] Already connected');
      return;
    }

    this.onConnectedCallback = callbacks.onConnected;
    this.onStartCallback = callbacks.onStart;
    this.onTranscriptCallback = callbacks.onTranscript;
    this.onErrorCallback = callbacks.onError;
    this.onEndCallback = callbacks.onEnd;
    this.onDisconnectCallback = callbacks.onDisconnect;

    try {
      // 连接 WebSocket
      await this.connectWebSocket();
      console.log('[ASR] WebSocket connected successfully');
      this.onConnectedCallback?.();
    } catch (error) {
      console.error('[ASR] Failed to connect:', error);
      this.onErrorCallback?.(error as Error);
      throw error;
    }
  }

  /**
   * 断开 WebSocket 连接
   */
  disconnect(): void {
    console.log('[ASR] Disconnecting WebSocket...');

    // 停止心跳
    this.stopHeartbeat();

    // 如果正在录音，先停止
    if (this.isRecording) {
      this.stopRecording();
    }

    // 关闭 WebSocket
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close();
      }
      this.ws = null;
    }
  }

  /**
   * 开始录音（使用已有的 WebSocket 连接）
   */
  async startRecording(): Promise<void> {
    if (this.isRecording) {
      console.warn('[ASR] Already recording');
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected. Call connect() first.');
    }

    // 发送开始信号
    this.ws.send(
      JSON.stringify({
        type: 'control',
        action: 'start',
      }),
    );

    this.onStartCallback?.();

    try {
      // 1. 获取麦克风权限
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // 单声道
          sampleRate: this.SAMPLE_RATE, // 16kHz 采样率
          echoCancellation: true, // 回声消除
          noiseSuppression: true, // 噪声抑制
          autoGainControl: true, // 自动增益
        },
      });

      // 2. 创建 AudioContext（如果还没有）
      if (!this.audioContext) {
        this.audioContext = new (
          window.AudioContext || (window as any).webkitAudioContext
        )({
          sampleRate: this.SAMPLE_RATE,
        });

        console.log(
          '[ASR] AudioContext created, sample rate:',
          this.audioContext.sampleRate,
        );
      }

      // 3. 创建音频输入源
      this.audioInput = this.audioContext.createMediaStreamSource(
        this.audioStream,
      );

      // 4. 创建处理器节点 (ScriptProcessorNode)
      this.processor = this.audioContext.createScriptProcessor(
        this.BUFFER_SIZE,
        this.CHANNELS, // 单声道输入
        this.CHANNELS, // 单声道输出
      );

      // 5. 处理音频数据
      this.processor.onaudioprocess = (e) => {
        if (
          !this.isRecording ||
          !this.ws ||
          this.ws.readyState !== WebSocket.OPEN
        ) {
          return;
        }

        // 获取音频数据 (Float32Array, 范围 -1.0 到 1.0)
        const inputData = e.inputBuffer.getChannelData(0);

        // 转换为 PCM 16位整数
        const pcmData = this.floatTo16BitPCM(inputData);

        // 将新数据追加到缓冲区
        const newBuffer = new Int16Array(
          this.audioBuffer.length + pcmData.length,
        );
        newBuffer.set(this.audioBuffer, 0);
        newBuffer.set(pcmData, this.audioBuffer.length);
        this.audioBuffer = newBuffer;

        // 当缓冲区数据足够时，按 CHUNK_SIZE 分块发送
        while (this.audioBuffer.length >= this.CHUNK_SIZE) {
          // 取前 CHUNK_SIZE 个样本
          const chunk = this.audioBuffer.slice(0, this.CHUNK_SIZE);

          // 更新缓冲区，移除已发送的数据
          this.audioBuffer = this.audioBuffer.slice(this.CHUNK_SIZE);

          // 发送分块数据
          this.sendPCMData(chunk);
        }
      };

      // 6. 连接音频处理链
      this.audioInput.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      // 7. 清空缓冲区
      this.audioBuffer = new Int16Array(0);

      this.isRecording = true;
      console.log('[ASR] Recording started');
    } catch (error) {
      console.error('[ASR] Failed to start recording:', error);
      this.onErrorCallback?.(error as Error);
      this.cleanupAudioResources();
      throw error;
    }
  }

  /**
   * 停止录音（保持 WebSocket 连接）
   */
  stopRecording(): void {
    console.log('[ASR] Stopping recording...');

    this.isRecording = false;

    // 断开音频处理链
    if (this.processor) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
      this.processor = null;
    }

    if (this.audioInput) {
      this.audioInput.disconnect();
      this.audioInput = null;
    }

    // 停止音频流
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.audioStream = null;
    }

    // 发送剩余缓冲区数据
    if (this.audioBuffer.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      console.log(`[ASR] Sending remaining ${this.audioBuffer.length} samples`);
      this.sendPCMData(this.audioBuffer);
      this.audioBuffer = new Int16Array(0);
    }

    // 发送停止信号（但不关闭 WebSocket）
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'control',
          action: 'stop',
        }),
      );
    }

    console.log('[ASR] Recording stopped, WebSocket still connected');
    this.onEndCallback?.();
  }

  /**
   * 将 Float32Array 转换为 16位 PCM (Int16Array)
   */
  private floatTo16BitPCM(float32Array: Float32Array): Int16Array {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      // 将 -1.0 到 1.0 的浮点数转换为 -32768 到 32767 的整数
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return int16Array;
  }

  /**
   * 将 Int16Array 转换为 Base64
   */
  private int16ArrayToBase64(int16Array: Int16Array): string {
    // 将 Int16Array 转换为 Uint8Array (小端序)
    const uint8Array = new Uint8Array(int16Array.buffer);

    // 转换为 base64
    let binary = '';
    const len = uint8Array.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }
    return btoa(binary);
  }

  /**
   * 发送 PCM 音频数据（分块）
   */
  private sendPCMData(pcmData: Int16Array): void {
    try {
      if (this.ws?.readyState === WebSocket.OPEN) {
        const base64 = this.int16ArrayToBase64(pcmData);

        // 发送 base64 编码的 PCM 数据
        // 每块 1600 样本 = 3200 字节 → base64 后约 4.3KB
        this.ws.send(
          JSON.stringify({
            type: 'audio',
            data: base64,
            sampleRate: this.SAMPLE_RATE,
          }),
        );

        console.log(
          `[ASR] Sent chunk: ${pcmData.length} samples (${
            pcmData.length * 2
          } bytes, base64: ${base64.length} chars)`,
        );
      }
    } catch (error) {
      console.error('[ASR] Failed to send PCM data:', error);
      this.onErrorCallback?.(new Error('Failed to send audio data'));
    }
  }

  /**
   * 清理音频资源（内部方法）
   */
  private cleanupAudioResources(): void {
    console.log('[ASR] Cleaning audio resources...');

    // 停止音频流
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.audioStream = null;
    }

    // 清理音频处理节点
    if (this.processor) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
      this.processor = null;
    }

    if (this.audioInput) {
      this.audioInput.disconnect();
      this.audioInput = null;
    }

    // 清空音频缓冲区
    this.audioBuffer = new Int16Array(0);
  }

  /**
   * 完全清理所有资源
   */
  cleanup(): void {
    console.log('[ASR] Cleaning up all resources...');

    // 停止心跳
    this.stopHeartbeat();

    // 停止录音
    if (this.isRecording) {
      this.isRecording = false;
    }

    // 清理音频资源
    this.cleanupAudioResources();

    // 关闭 WebSocket
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close();
      }
      this.ws = null;
    }

    // 关闭 AudioContext
    if (this.audioContext) {
      this.audioContext.close().catch((err) => {
        console.warn('[ASR] Failed to close AudioContext:', err);
      });
      this.audioContext = null;
    }

    // 调用结束回调
    this.onDisconnectCallback?.();
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * 检查是否正在录音
   */
  isRecordingActive(): boolean {
    return this.isRecording;
  }

  /**
   * 检查浏览器是否支持
   */
  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      (window.AudioContext || (window as any).webkitAudioContext) &&
      typeof window.WebSocket === 'function'
    );
  }
}
